import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PhieuNhapKho, PhieuNhapKhoInput } from '@/types/schemas';
import { transformDataFromHoaDon } from '../utils/transform-data';
import { useRouter, useSearchParams } from '@/routing';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

export function useInvoiceParamsPresence() {
  const searchParams = useSearchParams();
  return useMemo(() => {
    const hasInvoiceParams = Boolean(searchParams.get('hdmh'));
    const hasEditParam = Boolean(searchParams.get('edit'));
    return hasInvoiceParams && !hasEditParam;
  }, [searchParams]);
}

interface UseMultipleFormsArgs {
  addPhieuNhapKho: (data: PhieuNhapKhoInput) => Promise<PhieuNhapKho>;
}

export function useMultipleForms({ addPhieuNhapKho }: UseMultipleFormsArgs) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { entity } = useAuth();
  const hasInvoiceParams = useInvoiceParamsPresence();

  const [invoiceData, setInvoiceData] = useState<PhieuNhapKho[]>([]);
  const [showMultipleForms, setShowMultipleForms] = useState<boolean>(hasInvoiceParams);
  const [currentFormIndex, setCurrentFormIndex] = useState<number>(0);
  const [isLoadingInvoiceData, setIsLoadingInvoiceData] = useState<boolean>(false);

  // Comment out tài khoản mặc định - không cần thiết cho phieu-nhap-kho
  // const { data: taiKhoan, isLoading: isLoadingTaiKhoan } = useTaiKhoan({
  //   ma_tk: '1561',
  //   entity_slug: entity?.slug
  // });

  useEffect(() => {
    const hdmhParam = searchParams.get('hdmh');
    const editParam = searchParams.get('edit');

    const fetchInvoiceFromAPI = async (uuid: string) => {
      if (!entity?.slug) return;

      setIsLoadingInvoiceData(true);
      try {
        const response = await api.get(
          `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/${uuid}/`
        );
        const invoiceDetail = response.data;
        setInvoiceData([invoiceDetail]);
        setCurrentFormIndex(0);

        // Save to sessionStorage for future use
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(`hoa_don_detail_${uuid}`, JSON.stringify(invoiceDetail));
        }
      } catch (error) {
        console.error('Error fetching invoice data:', error);
        setInvoiceData([]);
      } finally {
        setIsLoadingInvoiceData(false);
      }
    };

    if (hdmhParam) {
      if (editParam) {
        setShowMultipleForms(false);
      } else {
        setShowMultipleForms(true);

        if (typeof window !== 'undefined') {
          const cachedData = sessionStorage.getItem(`hoa_don_detail_${hdmhParam}`);
          if (cachedData) {
            try {
              const parsedData = JSON.parse(cachedData);
              setInvoiceData([parsedData]);
              setCurrentFormIndex(0);
            } catch (error) {
              // If parsing fails, try to fetch from API
              fetchInvoiceFromAPI(hdmhParam);
            }
          } else {
            // No cached data, fetch from API
            fetchInvoiceFromAPI(hdmhParam);
          }
        }
      }
    }
  }, [searchParams, entity?.slug]);

  const initialData = useMemo(() => {
    const currentInvoice = invoiceData[currentFormIndex];
    if (!currentInvoice) return {};

    const hdmhUuid = searchParams.get('hdmh');

    let baseData = {};
    if (hdmhUuid && currentInvoice.uuid === hdmhUuid) {
      baseData = transformDataFromHoaDon(currentInvoice);
    }

    return baseData;
  }, [invoiceData, currentFormIndex, searchParams]);

  const handleMultipleFormSubmit = useCallback(
    async (data: PhieuNhapKhoInput) => {
      try {
        await addPhieuNhapKho(data);
        if (currentFormIndex < invoiceData.length - 1) {
          setShowMultipleForms(true);
          setCurrentFormIndex(currentFormIndex + 1);
        } else {
          setShowMultipleForms(false);
          setInvoiceData([]);
          setCurrentFormIndex(0);
          router.back();
        }
      } catch (error) {
        return;
      }
    },
    [addPhieuNhapKho, currentFormIndex, invoiceData.length, router]
  );

  const handleCloseMultipleForms = useCallback(() => {
    const hdmhUuid = searchParams.get('hdmh');
    if (hdmhUuid && typeof window !== 'undefined') {
      sessionStorage.removeItem(`hoa_don_detail_${hdmhUuid}`);
    }
    setShowMultipleForms(false);
    setInvoiceData([]);
    setCurrentFormIndex(0);
    router.back();
  }, [router, searchParams]);

  const invoiceInfo = useMemo(() => {
    const hdmhParam = searchParams.get('hdmh');
    const editParam = searchParams.get('edit');
    if (hdmhParam && !editParam) {
      return { nguon_hoa_don_id: hdmhParam };
    }
    return undefined;
  }, [searchParams]);

  return {
    hasInvoiceParams,
    showMultipleForms,
    invoiceData,
    currentFormIndex,
    initialData,
    handleMultipleFormSubmit,
    handleCloseMultipleForms,
    invoiceInfo,
    isLoadingInvoiceData
  } as const;
}
